import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

function CandidateForm() {
  const { jobId } = useParams(); // 🔍 Récupération de l’ID de l’offre depuis l’URL
  const [offer, setOffer] = useState(null); // 💼 Offre d'emploi à afficher
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    source: '',
    extra_doc_link: '' // 🌐 Lien complémentaire (ex: portfolio)
  });

  const [fileCV, setFileCV] = useState(null); // 📎 Fichier CV
  const [fileLetter, setFileLetter] = useState(null); // 📎 Lettre de motivation
  const [extraFile, setExtraFile] = useState(null); // 📎 Document complémentaire
  const [message, setMessage] = useState(''); // ✅ Message de confirmation ou erreur
  const [fileError, setFileError] = useState(''); // ❌ Erreur fichier

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

  const validateFile = (file) => {
    if (!file) return true;

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return 'Seuls les fichiers PDF, DOC, et DOCX sont autorisés.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return `La taille du fichier ne doit pas dépasser 5 MB. Taille actuelle: ${(file.size / (1024 * 1024)).toFixed(2)} MB`;
    }

    return null;
  };

  // 🔁 Charger les infos de l’offre lors du montage
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_DOMAINE}/api/jobs/${jobId}`);
        setOffer(res.data); // 🧠 Stocker les infos de l'offre
      } catch (err) {
        console.error("Erreur chargement de l'offre :", err.message);
      }
    };

    fetchOffer();
  }, [jobId]);

  // 🖊️ Mise à jour des champs texte du formulaire
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFileError('');
    setMessage('');
  };

  const handleFileChange = (e, setFile) => {
    const file = e.target.files[0];
    const error = validateFile(file);

    if (error) {
      setFileError(error);
      e.target.value = '';
      setFile(null);
    } else {
      setFileError('');
      setFile(file);
    }
  };

  // 📤 Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setFileError('');

    // Validate all files before submit
    const cvError = validateFile(fileCV);
    const letterError = validateFile(fileLetter);
    const extraError = validateFile(extraFile);

    if (cvError || letterError || extraError) {
      setFileError(cvError || letterError || extraError);
      return;
    }

    const data = new FormData(); // 📦 Crée un objet FormData pour envoyer des fichiers

    // 🔁 Ajouter les champs texte
    for (const key in formData) {
      data.append(key, formData[key]);
    }

    data.append('job_id', jobId); // 🧷 Lier le candidat à une offre
    if (fileCV) data.append('file_cv', fileCV);
    if (fileLetter) data.append('file_cover_letter', fileLetter);
    if (extraFile) data.append('extra_doc_file', extraFile); // ✅ Fichier complémentaire

    try {
      await axios.post(`${process.env.REACT_APP_DOMAINE}/api/candidates`, data);
      setMessage('✅ Candidature envoyée avec succès !');

      // ✅ Réinitialiser le formulaire
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        city: '',
        country: '',
        source: '',
        extra_doc_link: ''
      });
      setFileCV(null);
      setFileLetter(null);
      setExtraFile(null);
    } catch (err) {
      console.error("Erreur envoi :", err.message);
      const errorMsg = err.response?.data?.error || "Erreur lors de l'envoi de la candidature";
      setMessage("❌ " + errorMsg);
    }
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-4">📨 Postuler à cette offre</h3>
      {message && <div className="alert alert-info">{message}</div>}
      {fileError && <div className="alert alert-danger">{fileError}</div>}

      <div className="row">
        {/* 👉 Colonne gauche : description de l'offre */}
        <div className="col-md-6">
          {offer ? (
            <div className="border p-3 rounded bg-light">
              <h4>{offer.title}</h4>
              <p><strong>Contrat :</strong> {offer.type_contrat}</p>
              <p><strong>Lieu :</strong> {offer.ville}, {offer.pays}</p>
              <p><strong>Filiale :</strong> {offer.filiale}</p>
              <p><strong>Description :</strong><br />{offer.description}</p>
            </div>
          ) : (
            <p>Chargement de l'offre...</p>
          )}
        </div>

        {/* 👉 Colonne droite : formulaire de candidature */}
        <div className="col-md-6">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="mb-2">
              <label>Nom complet <span style={{ color: 'red' }}>*</span></label>
              <input type="text" name="full_name" required className="form-control" value={formData.full_name} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label>Email <span style={{ color: 'red' }}>*</span></label>
              <input type="email" name="email" required className="form-control" value={formData.email} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label>Téléphone <span style={{ color: 'red' }}>*</span></label>
              <input type="text" name="phone" required className="form-control" value={formData.phone} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label>Ville <span style={{ color: 'red' }}>*</span></label>
              <input type="text" name="city" required className="form-control" value={formData.city} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label>Pays <span style={{ color: 'red' }}>*</span></label>
              <input type="text" name="country" required className="form-control" value={formData.country} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label>Provenance <span style={{ color: 'red' }}>*</span></label>
              <select
                name="source"
                className="form-select"
                value={formData.source}
                onChange={handleChange}
                required
              >
                <option value="">-- Sélectionnez une provenance --</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="HelloWork">HelloWork</option>
              </select>
            </div>


            <div className="mb-2">
              <label>CV (PDF, DOC, DOCX - max 5MB) <span style={{ color: 'red' }}>*</span></label>
              <input type="file" required className="form-control" accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setFileCV)} />
            </div>
            <div className="mb-2">
              <label>Lettre de motivation (PDF, DOC, DOCX - max 5MB)</label>
              <input type="file" className="form-control" accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setFileLetter)} />
            </div>
            <div className="mb-2">
              <label>Document complémentaire (PDF, DOC, DOCX - max 5MB)</label>
              <input type="file" className="form-control" accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setExtraFile)} />
            </div>
            <div className="mb-2">
              <label>Lien vers Portfolio / GitHub</label>
              <input type="text" name="extra_doc_link" className="form-control" value={formData.extra_doc_link} onChange={handleChange} />
            </div>

            <button type="submit" className="btn btn-success mt-3">Soumettre ma candidature</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CandidateForm;
